import CloseIcon from '@mui/icons-material/Close';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import {
    MouseEvent,
    PointerEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { vec3 } from 'gl-matrix';
import { isWithinClickSlop } from '~/containers/viewer/viewport/clickSlop';
import { Image3D } from '~/domain/patient/image3d';
import {
    gridNormalToVoxelCovector,
    MipPlaneIndicator,
    planeIndicatorOnMip,
} from '~/domain/services/mipPlaneIndicator';
import {
    AnteriorMipResult,
    computeAnteriorMipCancellable,
    mipRayMaximumVoxelIndexAt,
} from '~/domain/services/volumeMip';
import type { Environment } from '~/engine/data/Environment';
import type { PlaneView } from '~/engine/view/views/PlaneView';
import { useImageStreamProgress } from '~/hooks/image/useImageStreamProgress';

// The indicator colours are fixed, not theme tokens: they are drawn over
// the projection, whose background is always black, so they must read on
// black in either theme.
/** The exact-line indicator: the plane's footprint on the overview IS this
 *  line (the common aligned case, plus rolled and sagittal planes). */
const SLICE_LINE_STYLE = 'rgba(57, 255, 136, 0.95)';
const SLICE_LINE_WIDTH_PX = 1.5;
/**
 * The approximate-line indicator, for a tilted plane: dashed and dimmed so
 * it can never be mistaken for the exact marker, drawn where the plane
 * passes at the crosshair's depth (a note in the panel says so).
 */
const APPROXIMATE_LINE_STYLE = 'rgba(57, 255, 136, 0.55)';
const APPROXIMATE_LINE_DASH_PX: number[] = [4, 3];
/**
 * Height of the note strip shown BELOW the projection, outside the
 * anatomy, sized for the note text at two lines on a narrow panel. A host
 * that fits the pane into a fixed box keeps this much under the projection
 * so the note never crops it.
 */
export const NOTE_STRIP_HEIGHT_PX = 32;
/** Below this the projection is a strip the user cannot read; the panel
 *  reports the series as unavailable instead of drawing it. */
const MIN_PROJECTION_HEIGHT_PX = 16;
/**
 * The same readability floor for the other axis: a thin sagittal slab (a few
 * mm patient-L-R across a whole-body S-I extent) would otherwise pass the
 * height check and draw as a few-px-wide sliver presented as ready, the
 * exact state the height floor exists to prevent, rotated 90 degrees.
 */
const MIN_PROJECTION_WIDTH_PX = 16;

/**
 * Projections already computed, keyed by the image they were computed from.
 *
 * A WeakMap rather than a keyed cache: the entry is reachable only while the
 * Image3D itself is, so unloading a patient drops the projections with it and
 * no eviction policy is needed. Caching outside the component is what lets the
 * user close and reopen the panel without paying for the pass again: the
 * pane unmounts while hidden.
 */
const projectionCache = new WeakMap<Image3D, AnteriorMipResult>();

/**
 * Message per state that draws no projection. Written out rather than built
 * from the state name so every key stays greppable: a missing message id
 * falls back to English silently.
 *
 * `ready` appears because it reaches this branch too: a projection that
 * computed fine but has no room to draw is "not available" from where the
 * user sits, and the panel says so rather than rendering nothing.
 */
const MIP_UNAVAILABLE_MESSAGE_IDS: Record<
    Exclude<ComputeState, 'computing'>,
    string
> = {
    ready: 'series.mipPreview.unavailable',
    failed: 'series.mipPreview.unavailable',
    waitingForSlices: 'series.mipPreview.waitingForSlices',
    imageIncomplete: 'series.mipPreview.imageIncomplete',
};

type ComputeState =
    | 'computing'
    | 'ready'
    | 'failed'
    /** Slices are still arriving; projecting now would misrepresent the series. */
    | 'waitingForSlices'
    /** Retrieval ended short: no projection will ever represent this series. */
    | 'imageIncomplete';

interface SeriesMipPaneProps {
    /** The series projected. */
    readonly image: Image3D;
    /**
     * The Environment that draws the image: its plane-intersection point is
     * where the slice line is drawn, and the line follows the point. Absent,
     * the projection is drawn with no line, the way it is drawn while the
     * point lies outside the volume.
     */
    readonly environment: Environment | undefined;
    /**
     * The plane view whose plane the indicator localizes. Its orientation
     * is read live at draw time, so MPR rotation and tilted acquisitions
     * are honoured. Absent, the indicator marks a grid-aligned transversal
     * plane through the point.
     */
    readonly view?: PlaneView;
    /**
     * The width the pane fills, px. The projection is drawn narrower and
     * centred when holding its aspect within the height cap asks for it.
     */
    readonly widthPx: number;
    /**
     * The tallest the projection may be drawn, px: the room of the box the
     * pane is fitted into, less the note strip. Height wins over the width:
     * a series taller than the room is drawn narrower to hold its aspect,
     * never cropped or squashed, either of which would misrepresent the
     * anatomy.
     */
    readonly maxHeightPx: number;
    /** Called with the flat voxel index the clicked pixel shows, if set. */
    readonly onLocate?: (voxelIndex: number) => void;
}

/**
 * Anterior maximum-intensity projection of one series, with a line marking
 * where the displayed plane passes. When the plane is tilted, the line
 * cannot be exact (the plane's footprint is a band); it is then drawn
 * dashed and dimmed at the crosshair's depth, with a note saying so. It
 * answers "where in the series am I?" without the user scrolling to find a
 * landmark.
 *
 * It reads `Image3D.floatPixelData`, the array already on the GPU, so costs
 * no extra memory, and writes nothing. A click hands the voxel behind the
 * clicked pixel to `onLocate`, which decides what moves.
 *
 * Preview-grade, and deliberately not a DRR: intensities are collapsed by a
 * plain per-ray maximum with no attenuation model, and the greyscale comes from
 * an auto-derived percentile window. Nothing here is measurable.
 *
 * The projection is refused, and the pane says why, while the series is
 * still arriving or ended short: a projection of a partial volume reads its
 * gaps as anatomy (SR-WOR-H-37-3).
 */
export default function SeriesMipPane({
    image,
    environment,
    view,
    widthPx,
    maxHeightPx,
    onLocate,
}: SeriesMipPaneProps) {
    const intl = useIntl();
    const progress = useImageStreamProgress(image);

    const [mip, setMip] = useState<AnteriorMipResult | null>(null);
    const [computeState, setComputeState] = useState<ComputeState>('computing');
    // The note shown under the indicator: 'tilted' while the line is the
    // approximate (crosshair-depth) one, 'parallel' while no line can be
    // truthfully drawn at all. The states must be SAID: a dashed line
    // without its explanation is a puzzle, and silence already means
    // "outside the volume".
    const [note, setNote] = useState<'tilted' | 'parallel' | null>(null);
    // The user may dismiss a note they have read, but per OCCURRENCE, not
    // for good: the dismissal resets whenever the note state changes, so
    // each newly arising condition announces itself again. A permanently
    // silenced "this line is approximate" would defeat the note's purpose.
    const [isNoteDismissed, setIsNoteDismissed] = useState(false);
    useEffect(() => {
        setIsNoteDismissed(false);
    }, [note]);
    const isNoteVisible = note !== null && !isNoteDismissed;
    // The canvases are held in STATE, not refs, and attached by callback ref,
    // so a remounted canvas element is a dependency change: whatever is on
    // screen is always painted onto the canvas currently there.
    const [imageCanvas, setImageCanvas] = useState<HTMLCanvasElement | null>(
        null
    );
    const [overlayCanvas, setOverlayCanvas] =
        useState<HTMLCanvasElement | null>(null);

    // Physical aspect, so an anisotropic or long series is drawn in
    // proportion: the projection is voxel-indexed, and voxels are rarely
    // square.
    const aspect = mip !== null ? mip.heightMm / mip.widthMm : 1;
    // The DRAWN width. Floored at 1px: rounding the height cap down can
    // otherwise reach zero on an extreme aspect, and an invisible panel is
    // the one outcome worse than a squat one: the user would read it as the
    // overview having no content.
    //
    // The pane is always `widthPx` wide; when the aspect caps the drawing
    // below that, the projection is letterboxed inside it (the host's
    // background matches the projection background by design). Feeding the
    // capped width back to the host would break a host that sizes itself
    // from the width it asked for.
    const drawWidth =
        mip !== null
            ? Math.max(1, Math.min(widthPx, Math.round(maxHeightPx / aspect)))
            : widthPx;
    // An extreme aspect, a single-slice series across a wide field of view
    // or a thin slab across a whole-body extent, rounds the projection down
    // to a strip a few px tall (or wide), which `overflow: hidden` then
    // renders as a panel showing nothing. Reported as unavailable rather
    // than drawn: this is an orientation aid, and an unreadable strip
    // presented as ready is the worse of the two outcomes. Falls back to
    // the square the other states use, so the message has room. The height
    // is clamped to the cap because rounding up from a capped width can
    // overshoot it by a pixel or two.
    const projectedHeight =
        mip !== null
            ? Math.min(maxHeightPx, Math.round(drawWidth * aspect))
            : drawWidth;
    const isShowable =
        computeState === 'ready' &&
        projectedHeight >= MIN_PROJECTION_HEIGHT_PX &&
        drawWidth >= MIN_PROJECTION_WIDTH_PX;
    // The message box is the square of the pane's width, cut to the cap so
    // it fits the host's box as the projection would.
    const height = isShowable
        ? projectedHeight
        : Math.min(widthPx, maxHeightPx);

    // Project the volume once per image. The cancellable pass runs in chunks
    // with a yield between them, so the spinner paints, scrolling stays
    // responsive, and an image switch mid-pass aborts the stale computation
    // instead of paying for the whole volume first.
    useEffect(() => {
        // A projection off a partly arrived volume is a picture of the
        // slices that happened to have landed: the gaps read as anatomy
        // rather than as absence, and the auto window is derived from a
        // subset. It is also the localizer the user reads slice position
        // off, so getting it wrong is not cosmetic. Refuse rather than
        // draw, and do NOT cache: the cache is keyed by image identity,
        // which a progressive `Image3D` keeps as it fills, so one partial
        // result would survive completion and never be recomputed
        // (SR-WOR-H-37-3).
        if (!progress.isComplete) {
            setMip(null);
            setComputeState(
                progress.isIncompleteFinal
                    ? 'imageIncomplete'
                    : 'waitingForSlices'
            );
            return;
        }

        const cached = projectionCache.get(image);
        if (cached) {
            setMip(cached);
            setComputeState('ready');
            return;
        }
        setMip(null);
        setComputeState('computing');
        let cancelled = false;
        void (async () => {
            try {
                const grid = image.grid;
                const result = await computeAnteriorMipCancellable(
                    {
                        voxels: image.floatPixelData,
                        sizeVoxels: [grid.sizeX, grid.sizeY, grid.sizeZ],
                        spacingMm: [grid.resX, grid.resY, grid.resZ],
                        orientation: grid.getOrientation(),
                    },
                    () => cancelled
                );
                if (result === null || cancelled) {
                    return;
                }
                projectionCache.set(image, result);
                setMip(result);
                setComputeState('ready');
            } catch (error) {
                // A series this cannot be projected from is not an error the
                // user can act on, and the pane is an aid: report the state
                // in the pane and leave the viewport alone.
                console.warn('SeriesMipPane: projection failed', error);
                if (!cancelled) {
                    setComputeState('failed');
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [image, progress.isComplete, progress.isIncompleteFinal]);

    // Voxel-resolution source canvas, built ONCE per projection. Scaling goes
    // through it because putImageData ignores the destination transform, and
    // rebuilding it per resize frame (canvas + ImageData + putImageData) is
    // exactly the per-frame cost drawImage below avoids.
    const sourceCanvas = useMemo(() => {
        if (!mip) {
            return null;
        }
        const source = document.createElement('canvas');
        source.width = mip.widthPx;
        source.height = mip.heightPx;
        const sourceContext = source.getContext('2d');
        if (!sourceContext) {
            return null;
        }
        sourceContext.putImageData(
            new ImageData(mip.rgba, mip.widthPx, mip.heightPx),
            0,
            0
        );
        return source;
    }, [mip]);

    // Draw (scale) the projection onto the displayed canvas.
    useEffect(() => {
        const canvas = imageCanvas;
        if (!canvas || !sourceCanvas) {
            return;
        }
        const context = canvas.getContext('2d');
        if (!context) {
            return;
        }
        context.clearRect(0, 0, canvas.width, canvas.height);
        context.drawImage(sourceCanvas, 0, 0, canvas.width, canvas.height);
    }, [imageCanvas, sourceCanvas, drawWidth, height]);

    const drawSliceLine = useCallback((): void => {
        const canvas = overlayCanvas;
        if (!canvas || !mip) {
            return;
        }
        const context = canvas.getContext('2d');
        if (!context) {
            return;
        }
        context.clearRect(0, 0, canvas.width, canvas.height);
        if (!environment) {
            // No Environment draws this image, so no plane passes through
            // it: the projection stands alone, as it does while the plane
            // sits outside the volume.
            setNote(null);
            return;
        }

        const grid = image.grid;
        const pointVoxel = grid.toVoxelSpace(
            environment.planeIntersectionPoint.coordinates
        );
        // The displayed plane's normal is the view's depth axis (third
        // column of its ViewSpace -> PatientSpace orientation), read LIVE
        // so MPR rotation is honoured, rotated into the grid frame and
        // handed to the domain covector transform. Without a view, fall
        // back to a grid-aligned transversal plane.
        let normalVoxel: vec3;
        if (view) {
            const orientation = view.getOrientation();
            const normalWorld = vec3.fromValues(
                orientation[6],
                orientation[7],
                orientation[8]
            );
            normalVoxel = gridNormalToVoxelCovector(
                grid.rotateToGrid(normalWorld),
                [grid.resX, grid.resY, grid.resZ]
            );
        } else {
            normalVoxel = vec3.create();
            normalVoxel[mip.rowAxis] = 1;
        }

        const indicator: MipPlaneIndicator = planeIndicatorOnMip(
            mip,
            [grid.sizeX, grid.sizeY, grid.sizeZ],
            pointVoxel,
            normalVoxel
        );
        setNote(
            indicator.kind === 'parallel'
                ? 'parallel'
                : indicator.kind === 'line' && !indicator.exact
                  ? 'tilted'
                  : null
        );
        if (indicator.kind !== 'line') {
            // Outside the volume, or (near-)parallel to the overview: no
            // line can be truthfully drawn. `parallel` additionally shows
            // its explicit note (state set above).
            return;
        }

        const toCanvas = (
            point: readonly [number, number]
        ): [number, number] => [
            (point[0] / mip.widthPx) * canvas.width,
            (point[1] / mip.heightPx) * canvas.height,
        ];
        const [x1, y1] = toCanvas(indicator.fromPx);
        const [x2, y2] = toCanvas(indicator.toPx);
        if (indicator.exact) {
            // The plane's footprint on the overview IS this line (aligned,
            // rolled, or sagittal plane).
            context.strokeStyle = SLICE_LINE_STYLE;
            context.lineWidth = SLICE_LINE_WIDTH_PX;
            context.setLineDash([]);
        } else {
            // Tilted plane: the line marks where it passes at the
            // crosshair's depth. Dashed and dimmed so it cannot be read as
            // the exact marker; the note below the panel explains it.
            context.strokeStyle = APPROXIMATE_LINE_STYLE;
            context.lineWidth = SLICE_LINE_WIDTH_PX;
            context.setLineDash(APPROXIMATE_LINE_DASH_PX);
        }
        context.beginPath();
        context.moveTo(x1, y1);
        context.lineTo(x2, y2);
        context.stroke();
        context.setLineDash([]);
    }, [overlayCanvas, mip, environment, image, view]);

    const pressRef = useRef<[number, number] | null>(null);

    const handleProjectionPointerDown = (
        event: PointerEvent<HTMLCanvasElement>
    ): void => {
        pressRef.current =
            event.button === 0 ? [event.clientX, event.clientY] : null;
    };

    // The clicked pixel's own voxel, not a re-search, and only within the
    // click slop of the press.
    const handleProjectionClick = (
        event: MouseEvent<HTMLCanvasElement>
    ): void => {
        const press = pressRef.current;
        pressRef.current = null;
        if (
            !mip ||
            !onLocate ||
            !press ||
            !isWithinClickSlop(press, [event.clientX, event.clientY])
        ) {
            return;
        }
        const bounds = event.currentTarget.getBoundingClientRect();
        const voxelIndex = mipRayMaximumVoxelIndexAt(mip, [
            ((event.clientX - bounds.left) / bounds.width) * mip.widthPx,
            ((event.clientY - bounds.top) / bounds.height) * mip.heightPx,
        ]);
        if (voxelIndex !== null) {
            onLocate(voxelIndex);
        }
    };

    // Redraw on every plane change, and once whenever the projection or the
    // pane size changes.
    useEffect(() => {
        drawSliceLine();
    }, [drawSliceLine, drawWidth, height]);

    // The Environment's plane-intersection point notifies per scroll tick;
    // the observer is released beside where it is taken.
    useEffect(() => {
        if (!environment) {
            return;
        }
        const point = environment.planeIntersectionPoint;
        const observer = () => drawSliceLine();
        point.subscribe(observer);
        return () => point.unsubscribe(observer);
    }, [environment, drawSliceLine]);

    return (
        <Box data-testid="series-mip-pane" sx={{ width: widthPx }}>
            {!isShowable && (
                <Box
                    sx={{
                        width: '100%',
                        height,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: 1,
                        textAlign: 'center',
                    }}
                >
                    {computeState === 'computing' ? (
                        <CircularProgress size={20} />
                    ) : (
                        // Distinct from an all-black projection on purpose: a
                        // blank panel would read as "this series is empty".
                        // The two streaming states are worded apart because
                        // one resolves by waiting and the other never does.
                        <Typography variant="caption" color="text.secondary">
                            <FormattedMessage
                                id={MIP_UNAVAILABLE_MESSAGE_IDS[computeState]}
                                values={{
                                    loaded: progress.loadedSliceCount,
                                    total: progress.expectedSliceCount,
                                    unavailable: progress.unavailableSliceCount,
                                }}
                            />
                        </Typography>
                    )}
                </Box>
            )}
            {isShowable && (
                // Centered: when the aspect caps the drawing narrower than
                // the pane, the projection letterboxes on a background
                // identical to its own.
                <Box
                    sx={{
                        position: 'relative',
                        lineHeight: 0,
                        width: drawWidth,
                        mx: 'auto',
                    }}
                >
                    <canvas
                        ref={setImageCanvas}
                        width={drawWidth}
                        height={height}
                        style={{
                            display: 'block',
                            cursor: onLocate ? 'crosshair' : undefined,
                        }}
                        onPointerDown={handleProjectionPointerDown}
                        onClick={handleProjectionClick}
                        data-testid="series-mip-projection"
                        role="img"
                        aria-label={intl.formatMessage({
                            id: 'series.mipPreview.ariaLabel',
                        })}
                    />
                    <canvas
                        ref={setOverlayCanvas}
                        data-testid="series-mip-slice-line"
                        width={drawWidth}
                        height={height}
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            // A click on the line reaches the projection.
                            pointerEvents: 'none',
                        }}
                        aria-hidden
                    />
                </Box>
            )}
            {isShowable && isNoteVisible && (
                // In a strip BELOW the projection, so the anatomy is never
                // obstructed; the pane grows by the strip's height. The
                // dismiss control appears on hover (and on keyboard focus:
                // it stays focusable while invisible).
                <Box
                    sx={{
                        position: 'relative',
                        height: NOTE_STRIP_HEIGHT_PX,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: 0.5,
                        '&:hover .mip-note-dismiss, &:focus-within .mip-note-dismiss':
                            {
                                opacity: 1,
                            },
                    }}
                >
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        data-testid="series-mip-plane-note"
                        sx={{
                            textAlign: 'center',
                            fontSize: '0.65rem',
                            lineHeight: 1.25,
                        }}
                    >
                        <FormattedMessage
                            id={
                                note === 'tilted'
                                    ? 'series.mipPreview.planeTilted'
                                    : 'series.mipPreview.planeParallel'
                            }
                        />
                    </Typography>
                    <IconButton
                        className="mip-note-dismiss"
                        size="small"
                        aria-label={intl.formatMessage({
                            id: 'series.mipPreview.dismissNote',
                        })}
                        onClick={() => setIsNoteDismissed(true)}
                        sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            p: 0.25,
                            opacity: 0,
                            transition: 'opacity 120ms',
                        }}
                    >
                        <CloseIcon sx={{ fontSize: '0.9rem' }} />
                    </IconButton>
                </Box>
            )}
        </Box>
    );
}
